import { useEffect, useRef, useState } from 'react'
import { useChatStore } from '../state/chatStore'
import { useT } from '../lib/i18n'

/**
 * Rechts unten im Verlauf, wo gleich die eigene Nachricht erscheint: Solange
 * das Mikrofon offen ist, zeigt diese Blase, dass K4ia zuhört — mit Balken,
 * die der Stimme folgen, und einem Text, der einmal wechselt.
 *
 * Zwei Regeln, beide aus der Erfahrung mit der springenden Beschriftung:
 *
 * - **Die Balken folgen der echten Lautstärke** und pulsieren nicht von selbst.
 *   Sonst sähe Stille aus wie Sprechen, und niemand könnte erkennen, ob das
 *   Mikrofon überhaupt etwas mitbekommt.
 * - **Der Text kippt nur in eine Richtung.** „K4ia hört zu" bis zur ersten
 *   Silbe, dann „K4ia versteht dich" — und das bleibt so, bis die Nachricht
 *   im Verlauf steht. Ein Text, der im Takt der Silben hin- und herspringt,
 *   sieht nach Fehler aus.
 *
 * Nach dem Schließen des Mikrofons bleibt die Blase stehen, bis das Transkript
 * da ist (dann ist K4ia „denkt nach"). Sonst gäbe es eine Lücke, in der man
 * gesprochen hat und nichts sieht.
 */
export function ListeningBubble() {
  const micOpen = useChatStore((s) => s.micOpen)
  const micLevel = useChatStore((s) => s.micLevel)
  const avatarState = useChatStore((s) => s.avatarState)
  const t = useT()
  const [verstanden, setVerstanden] = useState(false)

  // Beginn einer neuen Aufnahme: wieder von vorn.
  useEffect(() => {
    if (micOpen) setVerstanden(false)
  }, [micOpen])

  useEffect(() => {
    if (micOpen && micLevel > SPRACHSCHWELLE) setVerstanden(true)
  }, [micOpen, micLevel])

  // Das Transkript ist da (thinking) oder das Gespräch vorbei: Blase weg.
  useEffect(() => {
    if (avatarState !== 'listening') setVerstanden(false)
  }, [avatarState])

  const sichtbar = micOpen || (verstanden && avatarState === 'listening')
  if (!sichtbar) return null

  return (
    <div className="flex justify-end" data-testid="listening-bubble">
      <div
        className="flex items-center gap-3 rounded-[1.375rem] rounded-br-md bg-accent px-4 py-3 text-on-accent"
        role="status"
        aria-live="polite"
      >
        <LevelBars level={micOpen ? micLevel : 0} />
        <span className="text-sm font-medium">{verstanden ? t.understanding : t.listening}</span>
      </div>
    </div>
  )
}

/** Ab hier gilt es als Sprechen; darunter ist Raum, Atmen, Rauschen. */
export const SPRACHSCHWELLE = 0.06

/**
 * Fünf Balken, aus dem Pegel abgeleitet: die mittleren reagieren am stärksten,
 * die äußeren gedämpft — so entsteht die Form einer Stimme, ohne Zufall.
 * Ein kurzer Nachlauf (Abklingen statt Sprung) nimmt das Zittern heraus,
 * ohne die Reaktion zu verzögern.
 */
function LevelBars({ level }: { level: number }) {
  const [glatt, setGlatt] = useState(0)
  const letzterRef = useRef(0)

  useEffect(() => {
    // Schnell hoch, langsam runter: Sprechen sieht man sofort, Pausen füllen
    // sich nicht mit Flackern.
    const naechster = level > letzterRef.current ? level : letzterRef.current * 0.7 + level * 0.3
    letzterRef.current = naechster
    setGlatt(naechster)
  }, [level])

  const pegel = Math.min(1, glatt * 2.2)
  return (
    <span className="flex h-5 items-center gap-[3px]" aria-hidden="true">
      {GEWICHTE.map((gewicht, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-current transition-[height] duration-75"
          style={{ height: `${Math.max(3, 4 + pegel * gewicht * 16)}px` }}
        />
      ))}
    </span>
  )
}

const GEWICHTE = [0.45, 0.8, 1, 0.8, 0.45]
